import { getAndFormat } from 'Common/formValues';
import { valueOrNA } from 'Common/valueOrNA';
import * as R from 'ramda';
import uuid from 'uuid/v1';

const impressions = (results, list = '') => {
  return results.map(result => {
    const payloadResult = gtmResult(result);
    if (list) {
      payloadResult.list = list;
    }
    payloadResult.position = results.indexOf(result);
    return payloadResult;
  });
};

const gtmResult = result => ({
  id: result.sourceId,
  name: valueOrNA(result.derivative, 'model'),
  price: valueOrNA(result, 'totalPrice').toString(),
  brand: valueOrNA(result.derivative, 'make'),
  category: valueOrNA(result, 'year').toString(),
  variant: dealerFormat(result.dealer),
  coupon: couponFormat(result)
});

const dealerFormat = dealer =>
  dealer.name
    ? `${dealer.name} - ${valueOrNA(dealer, 'city')}, ${valueOrNA(
        dealer,
        'county'
      )}`.toLowerCase()
    : '';

const couponFormat = result =>
  result.mileage && result.derivative
    ? `${result.mileage}-${valueOrNA(
        result.derivative,
        'fuelType'
      )}-${valueOrNA(result.derivative, 'transmission')}-${valueOrNA(
        result.derivative,
        'doors'
      )}`.toLowerCase()
    : '';

export const gtmAlsoConsiderClick = item => {
  const payload = {};
  const product = gtmResult(item);
  product.position = 0;
  payload.event = 'productClick';
  payload.ecommerce = {
    click: {
      actionField: {
        list: 'Classifieds - Also consider'
      },
      products: [product]
    }
  };
  return payload;
};

export const gtmDisplayDetails = item => {
  const payload = {};
  const product = gtmResult(item);
  product.position = 0;
  payload.event = 'CarDetails';
  payload.ecommerce = {
    impressions: impressions(
      R.take(4, item.related),
      'Classifieds - Also consider'
    ),
    detail: {
      actionField: {
        list: 'Classifieds Car Details'
      },
      products: [product]
    }
  };
  return payload;
};

export const gtmDetails = item => {
  const payload = {};
  payload.dlUCarMake = item.derivative && item.derivative.make;
  payload.dlUCarModel = item.derivative && item.derivative.model;
  payload.dlUCarPriceF = item.totalPrice ? item.totalPrice.toString() : '';
  payload.dlUCarPriceT = item.totalPrice ? item.totalPrice.toString() : '';
  payload.dlUCarType = item.derivative && item.derivative.category;
  payload.dlUCarCondition = item.isNearlyNew ? 'nearlynew' : 'used';
  payload.dlUCarFuelType =
    item.derivative && item.derivative.fuelType ? item.derivative.fuelType : '';
  payload.dlUCarGearbox = item.derivative && item.derivative.transmission;
  payload.dlUCarYearF = item.year ? item.year.toString() : '';
  payload.dlUCarYearT = item.year ? item.year.toString() : '';
  payload.dlUCarMileage = item.mileage ? item.mileage.toString() : '';
  payload.dlUCarFuelCons =
    item.technical && item.technical.ec_combined_mpg
      ? item.technical.ec_combined_mpg.toString()
      : '';
  payload.dlUCarCO2E =
    item.technical && item.technical.co2_gkm
      ? item.technical.co2_gkm.toString()
      : '';
  payload.dlUCarAnnualTax =
    item.derivative && item.derivative.annualTax
      ? item.derivative.annualTax.toString()
      : '';
  payload.dlUCarInsurance =
    item.technical && item.technical.insuranceGroup
      ? item.technical.insuranceGroup
      : '';
  payload.dlUCarColour = item.colour ? item.colour : '';
  payload.dlUCarDoors =
    item.derivative && item.derivative.doors
      ? item.derivative.doors.toString()
      : '';
  payload.dlUCarSeats =
    item.technical && item.technical.no_of_seats
      ? item.technical.no_of_seats.toString()
      : '';
  payload.dlUCarAcceleration =
    item.technical && item.technical['0_to_62_mph_secs']
      ? item.technical['0_to_62_mph_secs'].toString()
      : '';
  payload.dlUCarEngineSizeF =
    item.technical && item.technical.cc ? item.technical.cc.toString() : '';
  payload.dlUCarEngineSizeT =
    item.technical && item.technical.cc ? item.technical.cc.toString() : '';
  payload.dlUsedCarDealer =
    item.dealer && item.dealer.name ? item.dealer.name : '';
  return payload;
};

export const gtmDisplayResults = results => {
  const payload = {};
  payload.event = 'eeImpressionData';
  payload.ecommerce = {
    currencyCode: 'GBP',
    impressions: impressions(results, 'Classifieds Car Details')
  };
  return payload;
};

export const gtmResults = (formValues, dealerName, numberOfCarsFound) => {
  const payload = gtmPageviewData(formValues);
  if (dealerName) {
    payload.dlUsedCarDealer = dealerName.toLowerCase();
  }
  payload.dlUsedCarsFound = numberOfCarsFound;
  return payload;
};

export const gtmPageview = (eventName, url, gaCategory, total) => {
  const payload = {};
  if (total) {
    payload.dlUsedCarsFound = total;
  }
  payload.event = `Pageview${eventName}`;
  payload.url = url;

  // Let's also update the dl.page variable.
  payload.page = {
    type: `Classifieds${eventName}`,
    displayName: gaCategory
  };
  return payload;
};

export const gtmResultClick = item => {
  const payload = {};
  const product = gtmResult(item);
  product.position = 0;
  payload.event = 'productClick';
  payload.ecommerce = {
    click: {
      actionField: {
        list: 'Classifieds Car Details'
      },
      products: [product]
    }
  };
  return payload;
};

export const gtmSubmitForm = (item, formType) => {
  const payload = {};
  const product = gtmResult(item);
  product.position = 0;
  product.price = '0';
  product.quantity = 1;
  payload.event = 'transaction';
  payload.eventData = {
    // Switch the label based on whether it's a callback or message.
    label: `heycar-${formType}-dealer`
  };
  payload.ecommerce = {
    purchase: {
      actionField: {
        id: uuid(),
        affiliation: item.dealer ? item.dealer.name : '',
        coupon: item.dealer.distance ? item.dealer.distance.toString() : '',
        revenue: '0'
      },
      products: [product]
    }
  };
  payload.qs = item.qs;
  return payload;
};

export const gtmContactDealer = (item, label) => {
  const payload = {};
  const product = gtmResult(item);
  product.position = 0;
  product.quantity = 1;
  payload.event = 'addToCart';
  payload.eventData = {
    label
  };
  payload.ecommerce = {
    currencyCode: 'GBP',
    add: {
      products: [product]
    }
  };
  return payload;
};

const gtmPageviewData = formValues => {
  const payload = {};
  if (formValues.make) {
    payload.dlUCarMake = getAndFormat(formValues.make);
  }
  if (formValues.model) {
    payload.dlUCarModel = getAndFormat(formValues.model);
  }
  if (formValues.minPrice) {
    payload.dlUCarPriceF = getAndFormat(formValues.minPrice).toString();
  }
  if (formValues.maxPrice) {
    payload.dlUCarPriceT = getAndFormat(formValues.maxPrice).toString();
  }
  if (formValues.postCode) {
    payload.dlUCarPostcode = formValues.postCode;
  }
  if (formValues.withinDistance) {
    payload.dlUCarDistance = getAndFormat(formValues.withinDistance);
  }
  if (formValues.category) {
    payload.dlUCarType = getAndFormat(formValues.category);
  }
  if (formValues.colour) {
    payload.dlUCarColour = getAndFormat(formValues.colour);
  }
  if (formValues.seats) {
    payload.dlUCarSeats = getAndFormat(formValues.seats).toString();
  }
  if (formValues.insuranceGroup) {
    payload.dlUCarInsurance = getAndFormat(formValues.insuranceGroup);
  }
  if (formValues.doors) {
    payload.dlUCarDoors = getAndFormat(formValues.doors).toString();
  }
  if (formValues.gearBox) {
    payload.dlUCarGearbox = getAndFormat(formValues.gearBox);
  }
  if (formValues.mileage) {
    payload.dlUCarMileage = getAndFormat(formValues.mileage).toString();
  }
  if (formValues.fuelType) {
    payload.dlUCarFuelType = getAndFormat(formValues.fuelType);
  }
  if (formValues.acceleration) {
    payload.dlUCarAcceleration = getAndFormat(formValues.acceleration);
  }
  if (formValues.annualTax) {
    payload.dlUCarAnnualTax = getAndFormat(formValues.annualTax).toString();
  }
  if (formValues.yearFrom) {
    payload.dlUCarYearF = getAndFormat(formValues.yearFrom).toString();
  }
  if (formValues.yearTo) {
    payload.dlUCarYearT = getAndFormat(formValues.yearTo).toString();
  }
  if (formValues.fuelConsumption) {
    payload.dlUCarFuelCons = getAndFormat(
      formValues.fuelConsumption
    ).toString();
  }
  if (formValues.co2Emissions) {
    payload.dlUCarCO2E = getAndFormat(formValues.co2Emissions).toString();
  }
  if (formValues.engineSizeFrom) {
    payload.dlUCarEngineSizeF = getAndFormat(formValues.engineSizeFrom);
  }
  if (formValues.engineSizeTo) {
    payload.dlUCarEngineSizeT = getAndFormat(formValues.engineSizeTo);
  }
  if (formValues.used) {
    payload.dlUCarCondition = formValues.nearlynew ? 'used-nearlynew' : 'used';
  }
  if (formValues.nearlynew) {
    payload.dlUCarCondition = formValues.used ? 'used-nearlynew' : 'nearlynew';
  }
  return payload;
};

export const gtmBasicOptions = formValues => {
  const payload = {};
  if (formValues.make) {
    payload.dlUCarMake = getAndFormat(formValues.make);
  }
  if (formValues.model) {
    payload.dlUCarModel = getAndFormat(formValues.model);
  }
  if (formValues.minPrice) {
    payload.dlUCarPriceF = getAndFormat(formValues.minPrice).toString();
  }
  if (formValues.maxPrice) {
    payload.dlUCarPriceT = getAndFormat(formValues.maxPrice).toString();
  }
  if (formValues.postCode) {
    payload.dlUCarPostcode = formValues.postCode;
  }
  if (formValues.withinDistance) {
    payload.dlUCarDistance = getAndFormat(formValues.withinDistance);
  }
  return payload;
};

export const gtmFurtherOptions = formValues => {
  return gtmPageviewData(formValues);
};

export const gtmWinners = (title, model, yearFromTo) => ({
  dlUsedCarAWCategory: title,
  dlUsedCarAWModel: model,
  dlUsedCarAWYear: yearFromTo
});



// WEBPACK FOOTER //
// ./src/Common/gtmPayloads/index.js